///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Screen-space companion to the storm column block. While a column is
/// open it replaces the anchored hurricane card with a column docked to the
/// left edge: headline the instant the pill is pressed, then rows that fill
/// in as the IR frames, the wind fit and the forecast summary land. Spatial
/// facts stay in the 3D block; everything verbal and the intensity chart
/// live here. Phones get the same card as a bottom sheet.
///

import { formatTimeAgo } from "@/components/controls/anchored-panel/panelAtoms"
import { useIsMobile } from "@/components/utils/use-mobile"
import { HurricanesTable, parseWindProbability, stormCategoryLabel, stormSource, type StormProperties } from "@/earthbrowser/dataset-distribution/hurricanes/HurricanesTable"
import { cWindProbabilityThresholds, probabilityHex, thresholdLabel } from "@/earthbrowser/dataset-distribution/hurricanes/windProbability"
import { basinName } from "@/earthbrowser/dataset-distribution/hurricanes/StormFeature"
import { cWatchWarningStyle, stormCategoryHex } from "@/earthbrowser/dataset-distribution/hurricanes/stormDisplay"
import { closestApproach, type ClosestApproach } from "@/earthbrowser/dataset-distribution/hurricanes/stormInsights"
import { parseWatchWarnings } from "@/earthbrowser/dataset-distribution/hurricanes/HurricanesTable"
import { formatDistance } from "@/earthbrowser/utils/unitFormatters"
import { cLayersPanelEdgeInset, cLayersPanelWidth, useBottomChromeOffset, useChromeInsets } from "@/hooks/useBottomChromeOffset"
import { setCutoutHudCollapsed, setMobileInfoSheetOpen, setStormCutout, setStormWindProbabilityKt, type StormCutoutBestTrack, type StormCutoutForecast, type StormCutoutHeadline, type StormCutoutLightning, type StormCutoutState } from "@/redux/dialogSlice"
import { sendCameraCommand } from "@/redux/earthBrowserSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { cn } from "@/utils/utils"
import { ChevronDown, ChevronUp, Cuboid, X } from "lucide-react"
import { useEffect, useRef, useState, type MutableRefObject, type ReactNode, type TouchEvent } from "react"
import { CutoutHudChip } from "./CutoutHudChip"
import { knaffZehrPressureHpa } from "@/earthbrowser/dataset-distribution/hurricanes/column/stormColumnStats"
import { blockSentence, buoySentence, categoryLabel, changeSentence, displayPerKt, environmentSentences, forecastSentence, formatLocal, framesSentence, isRapidIntensification, lightningSentences, nextAdvisorySentence, shipsSentences, speedKt, speedUnit, speedValueKt, structureSentences, windPressureSentence, windProbabilitySentences, windSentences } from "./stormColumnText"

/** Column width; also published as the engine's left viewport inset (useViewportInsetsPublisher) */
const cHudWidth = cLayersPanelWidth
/** Clearance under the top-left Layers / Search buttons */
const cHudTop = 64
/** Vertical travel on the phone handle that counts as a swipe rather than a tap */
const cSheetSwipeThresholdPx = 24

export function StormColumnHud() {
	const cutout = useAppSelector(state => state.dialog.stormCutout)
	const isMobile = useIsMobile()
	const dispatch = useAppDispatch()
	const chromeLeft = useChromeInsets().left
	const bottomOffset = useBottomChromeOffset()
	// Phones: the sheet collapses to a one-line bar (the block and the
	// storm focus stay); it comes back expanded for a different storm
	const [collapsed, setCollapsed] = useState(false)
	const swipeStartY = useRef<number | null>(null)
	const stormId = cutout?.stormId
	useEffect(() => {
		setCollapsed(false)
	}, [stormId])

	// The expanded sheet shares the bottom edge with the time control, which
	// steps aside like it does for the info sheets; the collapsed bar sits
	// above the time control instead
	const sheetOpen = cutout !== null && isMobile && !collapsed
	const sheetFlag = useAppSelector(state => state.dialog.mobileInfoSheetOpen)
	useEffect(() => {
		if (!sheetOpen) return
		// Re-asserted on every change: the anchored card this HUD replaces
		// clears the flag in its own unmount cleanup, which can land after
		// this effect on the frame the column opens
		if (!sheetFlag) dispatch(setMobileInfoSheetOpen(true))
	}, [sheetOpen, sheetFlag, dispatch])
	useEffect(() => {
		if (!sheetOpen) return
		return () => {
			dispatch(setMobileInfoSheetOpen(false))
		}
	}, [sheetOpen, dispatch])

	if (!cutout) return null
	const accent = cutout.storm ? stormCategoryHex(cutout.storm.category, cutout.storm.maxWindKt) : "#5ebaff"
	const title = cutout.storm ? `${cutout.storm.classification} ${cutout.storm.name}` : "Loading…"
	/** The one action that closes the block and returns to the storm */
	const close = () => dispatch(setStormCutout(null))

	if (!isMobile && cutout.hudCollapsed) {
		return (
			<CutoutHudChip
				kind="Storm column"
				kindStyle={{ color: accent }}
				borderStyle={{ borderColor: `${accent}33` }}
				expandLabel="Show storm column details"
				closeLabel="Close storm column"
				closeTitle="Close the storm column and return to the storm"
				onExpand={() => dispatch(setCutoutHudCollapsed({ cutout: "storm", collapsed: false }))}
				onClose={close}
			>
				{title}
			</CutoutHudChip>
		)
	}

	if (isMobile && collapsed) {
		return (
			<div className="fixed inset-x-2 z-40 pointer-events-none" style={{ bottom: `calc(env(safe-area-inset-bottom, 0px) + ${bottomOffset}px)` }}>
				<div
					className={cn(
						"pointer-events-auto w-full flex items-center gap-2 pl-4 pr-2 py-1.5",
						"bg-black/65 backdrop-blur-xl border rounded-xl shadow-2xl",
						"motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-200"
					)}
					style={{ borderColor: `${accent}33` }}
				>
					<button
						onClick={() => setCollapsed(false)}
						className="flex-1 min-w-0 flex items-center gap-2 py-0.5 text-left"
						aria-label="Expand storm column details"
						aria-expanded={false}
					>
						<Cuboid className="w-3.5 h-3.5 shrink-0" style={{ color: accent }} />
						<span className="text-[11px] font-semibold uppercase tracking-[0.18em] shrink-0" style={{ color: accent }}>Storm column</span>
						<span className="text-xs text-slate-200 truncate">{title}</span>
						<ChevronUp className="w-4 h-4 text-slate-400 ml-auto shrink-0" />
					</button>
					<button
						onClick={close}
						className="w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition-colors shrink-0"
						aria-label="Close storm column"
						title="Close the storm column and return to the storm"
					>
						<X className="w-3.5 h-3.5" />
					</button>
				</div>
			</div>
		)
	}

	const card = (
		<div
			className={cn(
				"pointer-events-auto flex flex-col bg-black/65 backdrop-blur-xl border rounded-xl shadow-2xl overflow-hidden",
				"motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200",
				isMobile ? "motion-safe:slide-in-from-bottom-4" : "motion-safe:slide-in-from-left-4"
			)}
			style={{ borderColor: `${accent}33` }}
		>
			{isMobile && (
				<SheetHandle
					onCollapse={() => setCollapsed(true)}
					swipeStartY={swipeStartY}
				/>
			)}
			<HudHeader
				accent={accent}
				onHide={isMobile ? () => setCollapsed(true) : () => dispatch(setCutoutHudCollapsed({ cutout: "storm", collapsed: true }))}
				onCollapse={isMobile ? () => setCollapsed(true) : undefined}
			/>
			<div className="flex-1 min-h-0 overflow-y-auto overscroll-contain [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent] px-4 pb-4 space-y-3">
				<Headline cutout={cutout} accent={accent} />
				<Rows cutout={cutout} accent={accent} />
			</div>
		</div>
	)

	if (isMobile) {
		return (
			<div className="fixed inset-x-2 z-40 pointer-events-none" style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 8px)" }}>
				<div className="flex flex-col" style={{ maxHeight: "45vh" }}>{card}</div>
			</div>
		)
	}

	return (
		<div
			className="fixed z-40 pointer-events-none flex flex-col"
			style={{
				left: chromeLeft + cLayersPanelEdgeInset,
				top: cHudTop,
				width: cHudWidth,
				// Stop above the legend tray chips in the bottom-left corner
				maxHeight: `calc(100vh - ${cHudTop}px - 120px)`
			}}
		>
			{card}
		</div>
	)
}

/**
 * Phone sheet handle: tap or swipe down collapses the sheet to its bar
 * without closing the column (the block and the storm focus stay put)
 */
function SheetHandle({ onCollapse, swipeStartY }: { onCollapse: () => void; swipeStartY: MutableRefObject<number | null> }) {
	const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
		swipeStartY.current = event.touches[0]?.clientY ?? null
	}
	const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
		const startY = swipeStartY.current
		swipeStartY.current = null
		const endY = event.changedTouches[0]?.clientY
		if (startY === null || endY === undefined) return
		if (endY - startY > cSheetSwipeThresholdPx) {
			event.preventDefault()
			onCollapse()
		}
	}
	return (
		<div
			role="button"
			tabIndex={0}
			aria-label="Collapse storm column details"
			aria-expanded
			className="shrink-0 flex justify-center pt-2 pb-1 touch-none"
			onClick={onCollapse}
			onKeyDown={event => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault()
					onCollapse()
				}
			}}
			onTouchStart={handleTouchStart}
			onTouchEnd={handleTouchEnd}
		>
			<div className="h-1 w-10 rounded-full bg-white/30" />
		</div>
	)
}

/**
 * Panel header. Its X hides the panel (chip on desktop, bar on phones) and
 * leaves the block and the storm's focus alone; the bar/chip carries the
 * close. The phone header also gets a chevron for the same collapse
 */
function HudHeader({ accent, onHide, onCollapse }: { accent: string; onHide: () => void; onCollapse?: () => void }) {
	return (
		<div className={cn("shrink-0 flex items-center justify-between px-4 pb-1", onCollapse ? "pt-1" : "pt-3")}>
			<div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: accent }}>
				<Cuboid className="w-3.5 h-3.5" />
				Storm column
			</div>
			<div className="flex items-center gap-1">
				{onCollapse && (
					<button
						onClick={onCollapse}
						className="pointer-events-auto w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
						aria-label="Collapse storm column details"
						title="Collapse the details; the block stays"
					>
						<ChevronDown className="w-4 h-4" />
					</button>
				)}
				<button
					onClick={onHide}
					className="pointer-events-auto w-6 h-6 flex items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
					aria-label="Hide storm column details"
					title="Hide this panel; the block stays"
				>
					<X className="w-3.5 h-3.5" />
				</button>
			</div>
		</div>
	)
}

function Headline({ cutout, accent }: { cutout: StormCutoutState; accent: string }) {
	const h = cutout.storm
	if (!h) {
		return <Skeleton lines={2} wide />
	}
	const change = changeSentence(h)
	const rising = h.change24hKt !== null && h.change24hKt >= 5
	const falling = h.change24hKt !== null && h.change24hKt <= -5
	return (
		<div className="space-y-1.5">
			<div className="text-lg font-bold text-white leading-tight">{h.classification} {h.name}</div>
			<div className="flex items-center gap-2 flex-wrap">
				<span
					className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border"
					style={{ color: accent, borderColor: `${accent}66`, backgroundColor: `${accent}22` }}
				>
					{stormCategoryLabel({ category: h.category, max_wind_kt: h.maxWindKt })}
				</span>
				<span className="text-xs text-slate-400">
					{basinName(h.basin)}{h.advisoryNum ? ` · Advisory ${h.advisoryNum}` : ""} · {formatTimeAgo(new Date(h.advisoryDate))}
				</span>
			</div>
			<div className="flex items-baseline gap-3 flex-wrap">
				{h.maxWindKt !== null && <span className="text-2xl font-light text-white leading-none">{speedKt(h.maxWindKt)}</span>}
				{h.mslpMb !== null && <span className="text-base font-light text-slate-200 leading-none">{Math.round(h.mslpMb)} mb</span>}
				{change && (
					<span className={cn("text-xs font-semibold", rising ? "text-red-300" : falling ? "text-sky-300" : "text-slate-400")}>{change}</span>
				)}
				{isRapidIntensification(h) && (
					<span className="text-[10px] font-semibold px-1.5 py-px rounded-full border text-red-300 border-red-400/40 bg-red-500/15" title="Rapid intensification: at least 30 kt in 24 h (NHC definition)">RI</span>
				)}
			</div>
			{cutout.status === "error" && (
				<div className="text-xs text-red-300/80">Couldn't build the storm column for this storm.</div>
			)}
		</div>
	)
}

function Rows({ cutout, accent }: { cutout: StormCutoutState; accent: string }) {
	const s = cutout.summary
	const h = cutout.storm
	const stages = cutout.stages
	const { storm, approach } = useStormRow(cutout.stormId, s?.wind?.r34MeanKm)

	return (
		<>
			<Row label="Block" ready={s !== undefined} accent={accent}>
				{s && blockSentence(s)}
				<ViewPresets accent={accent} />
			</Row>
			<Row label="Cloud tops · from IR" ready={stages.frames && s !== undefined} accent={accent}>
				{s && framesSentence(s)}
				{s?.structure && structureSentences(s.structure, s.wind?.bestTrack?.eyeKm).map((line, i) => <div key={i}>{line}</div>)}
				{s?.structure && s.structure.coldFractionTrend.length >= 3 && <ColdFractionSparkline trend={s.structure.coldFractionTrend} accent={accent} />}
			</Row>
			<Row label="Surface wind · modeled" ready={stages.wind} accent={accent}>
				{s?.wind && h ? windSentences(s.wind, h).map((line, i) => <div key={i}>{line}</div>) : "No wind model: the advisory carries no sustained wind"}
				{s?.wind?.bestTrack && s.wind.bestTrack.rmwSeries.length >= 3 && <RmwSparkline bestTrack={s.wind.bestTrack} accent={accent} />}
			</Row>
			{h && (s?.lightning || !stages.lightning) && (
				<Row label="Lightning · GOES GLM" ready={stages.lightning && s?.lightning !== undefined} accent={accent}>
					{s?.lightning && lightningSentences(s.lightning, h).map((line, i) => <div key={i}>{line}</div>)}
					{s?.lightning && s.lightning.hourly.length >= 3 && s.lightning.total > 0 && <LightningBars lightning={s.lightning} accent={accent} />}
				</Row>
			)}
			{(s?.environment || s?.ships || !stages.sst) && (
				<Row label="Environment" ready={stages.sst || s?.ships !== undefined} accent={accent}>
					{s?.ships && shipsSentences(s.ships).map((line, i) => <div key={`ships${i}`}>{line}</div>)}
					{s?.environment && environmentSentences(s.environment).map((line, i) => <div key={i}>{line}</div>)}
					{!s?.environment && stages.sst && <div className="text-slate-500">Sea surface temperature not synced</div>}
				</Row>
			)}
			{s?.buoys && s.buoys.length > 0 && (
				<Row label="Buoys in the block" ready={stages.buoys} accent={accent}>
					{s.buoys.slice(0, 5).map(b => <div key={b.stationId}>{buoySentence(b)}</div>)}
					{s.buoys.length > 5 && <div className="text-slate-500">+{s.buoys.length - 5} more pinned in the block</div>}
				</Row>
			)}
			{h && s?.forecast && (
				<>
					<Row label={`Intensity · ${speedUnit()}`} ready={stages.forecast} accent={accent}>
						{forecastSentence(s.forecast, h)}
						<IntensityChart forecast={s.forecast} headline={h} accent={accent} />
						{s.forecast.categoryShare && s.forecast.categoryShare.length > 0 && <CategoryShare forecast={s.forecast} />}
					</Row>
					{s.forecast.windPressure.length >= 2 && (
						<Row label="Wind vs pressure" ready={stages.forecast} accent={accent}>
							{windPressureSentence(h)}
							<WindPressureChart forecast={s.forecast} headline={h} accent={accent} />
						</Row>
					)}
				</>
			)}
			{h && <WindProbabilityRow storm={storm} approach={approach} headline={h} accent={accent} />}
			<HazardsRow storm={storm} approach={approach} accent={accent} />
			{h && (
				<div className="text-[10px] text-slate-500 pt-1 border-t border-white/5 leading-snug">
					Measured: {s?.frames ? `${s.frames.satellite} IR, ` : ""}{s?.lightning && s.lightning.total > 0 ? "GOES GLM, " : ""}{stormSource(h.basin)} advisory{h.advisoryNum ? ` ${h.advisoryNum}` : ""}
					{s?.wind?.bestTrack ? ", NHC best track" : ""}{s?.wind ? " · Modeled: Holland wind fit" : ""}
					{s?.forecast?.ensembleRun ? " · Ensemble: ECMWF" : ""}
					{nextAdvisorySentence(h) ? ` · ${nextAdvisorySentence(h)}` : ""}
				</div>
			)}
		</>
	)
}

/** Camera presets for the block, routed to the focused column through the camera command chain */
function ViewPresets({ accent }: { accent: string }) {
	const dispatch = useAppDispatch()
	const presets: Array<{ id: "oblique" | "along" | "across" | "overhead"; label: string; title: string }> = [
		{ id: "oblique", label: "Oblique", title: "Three-quarter view of the block" },
		{ id: "along", label: "Along", title: "Edge-on to the along-motion section (cloud tops and wind through the center along the track)" },
		{ id: "across", label: "Across", title: "Edge-on to the across-motion section" },
		{ id: "overhead", label: "Overhead", title: "Straight down onto the wind field and the lid" },
	]
	return (
		<div className="flex items-center gap-1 pt-1">
			<span className="text-[10px] text-slate-500 mr-1">View</span>
			{presets.map(p => (
				<button
					key={p.id}
					onClick={() => dispatch(sendCameraCommand({ command: "column/view", data: { preset: p.id } }))}
					className="pointer-events-auto text-[10px] font-semibold px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.04] text-slate-300 hover:text-white hover:border-white/25 transition-colors"
					title={p.title}
					aria-label={`${p.label} view of the storm column`}
				>
					<span style={{ color: p.id === "oblique" ? accent : undefined }}>{p.label}</span>
				</button>
			))}
		</div>
	)
}

/**
 * The storm's row from the local table (the HUD state carries only the
 * headline) and the nearest significant city along its forecast
 */
function useStormRow(stormId: string, r34: number | undefined): { storm: StormProperties | null; approach: ClosestApproach | null } {
	const [storm, setStorm] = useState<StormProperties | null>(null)
	const [approach, setApproach] = useState<ClosestApproach | null>(null)
	useEffect(() => {
		let cancelled = false
		setStorm(null)
		setApproach(null)
		HurricanesTable.getById(stormId)
			.then(async row => {
				if (cancelled || !row) return
				setStorm(row)
				const result = await closestApproach(row, r34)
				if (!cancelled) setApproach(result)
			})
			.catch(() => { /* table may not be synced yet */ })
		return () => { cancelled = true }
	}, [stormId, r34])
	return { storm, approach }
}

/**
 * NHC 120 h wind speed probabilities: the chance of 34 / 50 / 64 kt winds
 * at the nearest city and along the track, a threshold switch that drives
 * the globe swath and the contours on the block floor, and the color key
 */
function WindProbabilityRow({ storm, approach, headline, accent }: { storm: StormProperties | null; approach: ClosestApproach | null; headline: StormCutoutHeadline; accent: string }) {
	const dispatch = useAppDispatch()
	const kt = useAppSelector(state => state.dialog.stormWindProbabilityKt)
	if (!storm) return null
	const wp = parseWindProbability(storm.wind_probability)
	if (!wp) return null
	const classes = [5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]
	return (
		<Row label="Wind probabilities · NHC 120 h" ready accent={accent}>
			{windProbabilitySentences(wp, kt, approach, headline).map((line, i) => <div key={i}>{line}</div>)}
			<div className="flex items-center gap-1 pt-1 flex-wrap">
				<span className="text-[10px] text-slate-500 mr-1">Show</span>
				{cWindProbabilityThresholds.map(t => (
					<button
						key={t}
						onClick={() => dispatch(setStormWindProbabilityKt(t))}
						className={cn(
							"pointer-events-auto text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors",
							t === kt ? "border-white/30 bg-white/10 text-white" : "border-white/10 bg-white/[0.04] text-slate-300 hover:text-white hover:border-white/25"
						)}
						title={`Show the chance of ${thresholdLabel(t).toLowerCase()} winds on the globe and the block floor`}
						aria-label={`Show ${thresholdLabel(t)} wind probabilities`}
						aria-pressed={t === kt}
					>
						{t === 34 ? "TS force" : t === 50 ? "50 kt" : "Hurricane force"}
					</button>
				))}
			</div>
			<div className="flex items-center gap-px pt-1" aria-label="Probability color key, 5% to 100%">
				{classes.map(c => (
					<div key={c} className="flex-1 h-2 first:rounded-l last:rounded-r" style={{ backgroundColor: probabilityHex(c) }} title={`${c}%`} />
				))}
			</div>
			<div className="flex justify-between text-[10px] text-slate-500"><span>5%</span><span>50%</span><span>100%</span></div>
		</Row>
	)
}

/** Watches/warnings and the nearest significant city */
function HazardsRow({ storm, approach, accent }: { storm: StormProperties | null; approach: ClosestApproach | null; accent: string }) {
	const watchWarnings = storm ? parseWatchWarnings(storm.watches_warnings) : []
	if (storm && watchWarnings.length === 0 && !approach) return null
	return (
		<Row label="Hazards" ready={storm !== null} accent={accent}>
			{watchWarnings.length > 0 && (
				<div className="flex flex-wrap gap-1 pb-0.5">
					{watchWarnings.map(ww => {
						const style = cWatchWarningStyle[ww.code.toUpperCase()]
						if (!style) return null
						return (
							<span key={ww.code} className="text-[10px] font-semibold px-1.5 py-px rounded-full border" style={{ color: style.hex, borderColor: `${style.hex}66`, backgroundColor: `${style.hex}1e` }}>
								{style.label}
							</span>
						)
					})}
				</div>
			)}
			{approach?.arrivalT !== undefined && (
				<div>Tropical-storm-force winds reach <span className="text-slate-100">{approach.cityName}</span> {formatLocal(approach.arrivalT)} <span className="text-slate-500">(modeled 34-kt extent along the forecast)</span></div>
			)}
			{approach && (
				<div>Closest approach {formatDistance(approach.distanceKm)} {approach.direction} of {approach.cityName} · {formatLocal(approach.t)}</div>
			)}
		</Row>
	)
}

function Row({ label, ready, accent, children }: { label: string; ready: boolean; accent: string; children: ReactNode }) {
	return (
		<div className="space-y-1">
			<div className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: `${accent}cc` }}>{label}</div>
			{ready ? (
				<div className="text-xs text-slate-300 leading-snug space-y-0.5">{children}</div>
			) : (
				<Skeleton lines={1} />
			)}
		</div>
	)
}

function Skeleton({ lines, wide = false }: { lines: number; wide?: boolean }) {
	return (
		<div className="space-y-1.5 motion-safe:animate-pulse" aria-hidden>
			{Array.from({ length: lines }, (_, i) => (
				<div key={i} className={cn("h-3 rounded bg-white/10", i === 0 && wide ? "w-2/3" : "w-full")} />
			))}
		</div>
	)
}

// ---------------------------------------------------------------------------
// Deep-convection fraction over the loaded frames
// ---------------------------------------------------------------------------

function ColdFractionSparkline({ trend, accent }: { trend: Array<{ t: number; fraction: number }>; accent: string }) {
	const width = 288
	const height = 26
	const pad = 2
	const t0 = trend[0].t
	const t1 = trend[trend.length - 1].t
	const max = Math.max(...trend.map(p => p.fraction), 0.01)
	const x = (t: number) => pad + ((t - t0) / Math.max(t1 - t0, 1)) * (width - 2 * pad)
	const y = (f: number) => height - pad - (f / max) * (height - 2 * pad)
	const d = trend.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.t).toFixed(1)},${y(p.fraction).toFixed(1)}`).join(" ")
	return (
		<div className="pt-1">
			<div className="flex justify-between text-[10px] text-slate-500"><span>Deep-convection fraction</span><span>peak {(max * 100).toFixed(1)}%</span></div>
			<svg viewBox={`0 0 ${width} ${height}`} className="w-full block" style={{ height }} role="img" aria-label="Fraction of the IR frame colder than -70 °C over the loaded frames">
				<path d={d} fill="none" stroke={accent} strokeWidth="1.4" strokeLinejoin="round" />
			</svg>
		</div>
	)
}

// ---------------------------------------------------------------------------
// Radius of maximum wind along the best track (eyewall contraction / expansion)
// ---------------------------------------------------------------------------

function RmwSparkline({ bestTrack, accent }: { bestTrack: StormCutoutBestTrack; accent: string }) {
	const width = 288
	const height = 26
	const pad = 2
	const series = bestTrack.rmwSeries
	const t0 = series[0][0]
	const t1 = series[series.length - 1][0]
	const max = Math.max(...series.map(p => p[1]), 1)
	const x = (t: number) => pad + ((t - t0) / Math.max(t1 - t0, 1)) * (width - 2 * pad)
	const y = (km: number) => height - pad - (km / max) * (height - 2 * pad)
	const d = series.map((p, i) => `${i === 0 ? "M" : "L"}${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join(" ")
	return (
		<div className="pt-1">
			<div className="flex justify-between text-[10px] text-slate-500"><span>Radius of maximum wind · best track</span><span>{formatDistance(series[0][1])} → {formatDistance(series[series.length - 1][1])}</span></div>
			<svg viewBox={`0 0 ${width} ${height}`} className="w-full block" style={{ height }} role="img" aria-label="Radius of maximum wind along the NHC best track">
				<path d={d} fill="none" stroke={accent} strokeWidth="1.4" strokeLinejoin="round" />
				{series.map(p => <circle key={p[0]} cx={x(p[0])} cy={y(p[1])} r={1.6} fill={accent} />)}
			</svg>
			<div className="flex justify-between text-[10px] text-slate-500"><span>{formatLocal(t0)}</span><span>{formatLocal(t1)}</span></div>
		</div>
	)
}

// ---------------------------------------------------------------------------
// GLM flashes per hour, inner core stacked over the rainbands
// ---------------------------------------------------------------------------

function LightningBars({ lightning, accent }: { lightning: StormCutoutLightning; accent: string }) {
	const width = 288
	const height = 30
	const pad = 2
	const bins = lightning.hourly
	const max = Math.max(...bins.map(b => b.count), 1)
	const slot = (width - 2 * pad) / bins.length
	const barW = Math.max(1, slot - 1)
	const y = (count: number) => height - pad - (count / max) * (height - 2 * pad)
	return (
		<div className="pt-1">
			<div className="flex justify-between text-[10px] text-slate-500">
				<span>Flashes per hour · <span style={{ color: "#fde68a" }}>inner core</span> over rainbands</span>
				<span>peak {max}/h</span>
			</div>
			<svg viewBox={`0 0 ${width} ${height}`} className="w-full block" style={{ height }} role="img" aria-label="GLM flashes per hour over the loaded window, inner-core flashes stacked over rainband flashes">
				{bins.map((b, i) => {
					const x = pad + i * slot
					const last = i === bins.length - 1
					return (
						<g key={b.t} opacity={last ? 1 : 0.75}>
							<rect x={x} y={y(b.count)} width={barW} height={Math.max(0, y(0) - y(b.count))} fill={accent} opacity={0.45} />
							<rect x={x} y={y(b.innerCore)} width={barW} height={Math.max(0, y(0) - y(b.innerCore))} fill="#fde68a" opacity={0.9} />
						</g>
					)
				})}
			</svg>
			<div className="flex justify-between text-[10px] text-slate-500"><span>{formatLocal(bins[0].t)}</span><span>{formatLocal(bins[bins.length - 1].t)}</span></div>
		</div>
	)
}

// ---------------------------------------------------------------------------
// Intensity: observed, official forecast, ensemble plume over category bands
// ---------------------------------------------------------------------------

const cChartWidth = 288
const cChartHeight = 118
const cChartLeft = 26
const cChartRight = 22
const cChartTop = 6
const cChartBottom = 16
const cBands: Array<{ lo: number; hi: number; category: number; label: string }> = [
	{ lo: 34, hi: 64, category: 0, label: "TS" },
	{ lo: 64, hi: 83, category: 1, label: "C1" },
	{ lo: 83, hi: 96, category: 2, label: "C2" },
	{ lo: 96, hi: 113, category: 3, label: "C3" },
	{ lo: 113, hi: 137, category: 4, label: "C4" },
	{ lo: 137, hi: 175, category: 5, label: "C5" },
]

function IntensityChart({ forecast, headline, accent }: { forecast: StormCutoutForecast; headline: StormCutoutHeadline; accent: string }) {
	const clockMs = useAppSelector(state => state.earthbrowser.clockCurrentTime)
	const all = [...forecast.observed, ...forecast.forecast, ...(forecast.ensP10 ?? []), ...(forecast.ensP90 ?? []), ...(forecast.ships ?? [])]
	if (all.length < 2) return null
	const t0 = Math.min(...all.map(p => p[0]))
	const t1 = Math.max(...all.map(p => p[0]))
	const maxKt = Math.max(...all.map(p => p[1]), headline.maxWindKt ?? 0)
	const kt0 = 20
	const kt1 = Math.max(80, Math.ceil((maxKt + 10) / 20) * 20)
	const plotW = cChartWidth - cChartLeft - cChartRight
	const plotH = cChartHeight - cChartTop - cChartBottom
	const x = (t: number) => cChartLeft + plotW * (t - t0) / Math.max(t1 - t0, 1)
	const y = (kt: number) => cChartTop + plotH * (1 - (Math.min(kt, kt1) - kt0) / (kt1 - kt0))
	const path = (points: Array<[number, number]>) => points.map((p, i) => `${i === 0 ? "M" : "L"}${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join(" ")

	const lastObserved = forecast.observed.length > 0 ? forecast.observed[forecast.observed.length - 1] : undefined
	const forecastPts = lastObserved ? [lastObserved, ...forecast.forecast.filter(p => p[0] > lastObserved[0])] : forecast.forecast
	const plume = forecast.ensP10 && forecast.ensP90 && forecast.ensP10.length > 1
		? [...forecast.ensP90, ...[...forecast.ensP10].reverse()].map(p => `${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join(" ")
		: undefined
	// Round ticks in the display unit, mapped back to knots for placement
	const perKt = displayPerKt()
	const displayMax = kt1 * perKt
	const tickStep = displayMax > 220 ? 50 : displayMax > 120 ? 25 : 20
	const ktTicks: number[] = []
	for (let v = tickStep; v < displayMax; v += tickStep) {
		const kt = v / perKt
		if (kt > kt0 + 5) ktTicks.push(kt)
	}
	const nowX = clockMs >= t0 && clockMs <= t1 ? x(clockMs) : undefined
	const landfallX = forecast.landfallT !== undefined && forecast.landfallT >= t0 && forecast.landfallT <= t1 ? x(forecast.landfallT) : undefined

	return (
		<svg width={cChartWidth} height={cChartHeight} className="block" role="img" aria-label="Sustained wind: observed, official forecast and ensemble spread over Saffir-Simpson category bands">
			{cBands.filter(b => b.lo < kt1).map(b => {
				const hex = stormCategoryHex(b.category, b.lo)
				return (
					<g key={b.label}>
						<rect x={cChartLeft} y={y(Math.min(b.hi, kt1))} width={plotW} height={Math.max(0, y(b.lo) - y(Math.min(b.hi, kt1)))} fill={hex} opacity={0.09} />
						<text x={cChartWidth - cChartRight + 4} y={(y(b.lo) + y(Math.min(b.hi, kt1))) / 2 + 3} fontSize={8.5} fill={hex} opacity={0.9}>{b.label}</text>
					</g>
				)
			})}
			{ktTicks.map(k => (
				<g key={k}>
					<line x1={cChartLeft} x2={cChartWidth - cChartRight} y1={y(k)} y2={y(k)} stroke="rgba(255,255,255,0.06)" />
					<text x={cChartLeft - 4} y={y(k) + 3} textAnchor="end" fontSize={8.5} fill="rgba(148,163,184,0.9)">{speedValueKt(k)}</text>
				</g>
			))}
			{plume && <polygon points={plume} fill="#ffffff" opacity={0.10} />}
			{forecast.observed.length >= 2 && <path d={path(forecast.observed)} fill="none" stroke={accent} strokeWidth={1.6} strokeLinejoin="round" />}
			{forecastPts.length >= 2 && <path d={path(forecastPts)} fill="none" stroke={accent} strokeWidth={1.3} strokeDasharray="3 2" opacity={0.85} strokeLinejoin="round" />}
			{forecast.ships && forecast.ships.length >= 2 && <path d={path(forecast.ships)} fill="none" stroke="rgba(203,213,225,0.8)" strokeWidth={1} strokeDasharray="1.5 2.5" strokeLinejoin="round" />}
			{nowX !== undefined && <line x1={nowX} x2={nowX} y1={cChartTop} y2={cChartHeight - cChartBottom} stroke="rgba(255,255,255,0.45)" strokeWidth={0.8} />}
			{landfallX !== undefined && (
				<>
					<line x1={landfallX} x2={landfallX} y1={cChartTop} y2={cChartHeight - cChartBottom} stroke="rgba(224,28,36,0.8)" strokeWidth={0.8} strokeDasharray="2 2" />
					<text x={landfallX + 3} y={cChartHeight - 3} fontSize={8.5} fill="rgba(224,28,36,0.9)">landfall</text>
				</>
			)}
			<text x={cChartLeft} y={cChartHeight - 3} fontSize={8.5} fill="rgba(100,116,139,0.9)">{formatLocal(t0)}</text>
			<text x={cChartWidth - cChartRight} y={cChartHeight - 3} textAnchor="end" fontSize={8.5} fill="rgba(100,116,139,0.9)">{formatLocal(t1)}</text>
		</svg>
	)
}

// ---------------------------------------------------------------------------
// Wind vs pressure: observed pairs against the Knaff–Zehr climatology
// ---------------------------------------------------------------------------

const cWpHeight = 110
const cWpLeft = 30
const cWpRight = 8
const cWpTop = 6
const cWpBottom = 16

function WindPressureChart({ forecast, headline, accent }: { forecast: StormCutoutForecast; headline: StormCutoutHeadline; accent: string }) {
	const points = forecast.windPressure
	const kts = points.map(p => p[1])
	const kt0 = 20
	const kt1 = Math.max(100, Math.ceil((Math.max(...kts) + 15) / 20) * 20)
	const curve: Array<[number, number]> = []
	for (let kt = kt0; kt <= kt1; kt += 5) {
		curve.push([kt, knaffZehrPressureHpa(kt, headline.lat, headline.movementSpeedKt ?? 0, headline.basin)])
	}
	const pressures = [...points.map(p => p[2]), ...curve.map(c => c[1])]
	const p1 = Math.ceil((Math.max(...pressures) + 4) / 10) * 10
	const p0 = Math.floor((Math.min(...pressures) - 4) / 10) * 10
	const plotW = cChartWidth - cWpLeft - cWpRight
	const plotH = cWpHeight - cWpTop - cWpBottom
	const x = (kt: number) => cWpLeft + plotW * (kt - kt0) / (kt1 - kt0)
	const y = (hpa: number) => cWpTop + plotH * (p1 - hpa) / Math.max(p1 - p0, 1)
	const path = curve.map((c, i) => `${i === 0 ? "M" : "L"}${x(c[0]).toFixed(1)},${y(c[1]).toFixed(1)}`).join(" ")
	const tLast = points[points.length - 1][0]
	const tFirst = points[0][0]
	const ktTicks: number[] = []
	const perKt = displayPerKt()
	const step = kt1 * perKt > 150 ? 25 : 20
	for (let v = step; v < kt1 * perKt; v += step) {
		const kt = v / perKt
		if (kt > kt0 + 5) ktTicks.push(kt)
	}
	const hpaTicks: number[] = []
	for (let p = p0; p <= p1; p += p1 - p0 > 80 ? 40 : 20) hpaTicks.push(p)

	return (
		<svg width={cChartWidth} height={cWpHeight} className="block" role="img" aria-label="Observed sustained wind against minimum pressure, with the Knaff–Zehr climatological wind–pressure curve">
			{hpaTicks.map(p => (
				<g key={p}>
					<line x1={cWpLeft} x2={cChartWidth - cWpRight} y1={y(p)} y2={y(p)} stroke="rgba(255,255,255,0.06)" />
					<text x={cWpLeft - 4} y={y(p) + 3} textAnchor="end" fontSize={8.5} fill="rgba(148,163,184,0.9)">{p}</text>
				</g>
			))}
			{ktTicks.map(k => (
				<text key={k} x={x(k)} y={cWpHeight - 4} textAnchor="middle" fontSize={8.5} fill="rgba(148,163,184,0.9)">{speedValueKt(k)}</text>
			))}
			<path d={path} fill="none" stroke="rgba(148,163,184,0.7)" strokeWidth={1} strokeDasharray="3 2" />
			<text x={cChartWidth - cWpRight} y={y(curve[curve.length - 1][1]) - 4} textAnchor="end" fontSize={8} fill="rgba(148,163,184,0.8)">Knaff–Zehr</text>
			{points.map(([t, kt, hpa], i) => {
				const age = tLast > tFirst ? (t - tFirst) / (tLast - tFirst) : 1
				const last = i === points.length - 1
				return (
					<circle
						key={i}
						cx={x(kt)}
						cy={y(hpa)}
						r={last ? 3.5 : 2}
						fill={last ? "#ffffff" : accent}
						stroke={last ? accent : "none"}
						strokeWidth={last ? 1.5 : 0}
						opacity={last ? 1 : 0.3 + 0.6 * age}
					/>
				)
			})}
			<text x={cWpLeft - 4} y={cWpTop + 2} textAnchor="end" fontSize={8} fill="rgba(100,116,139,0.9)">hPa</text>
			<text x={cChartWidth - cWpRight} y={cWpHeight - 4} textAnchor="end" fontSize={8} fill="rgba(100,116,139,0.9)">{speedUnit()}</text>
		</svg>
	)
}

/** Modal category among ensemble members at the standard forecast hours */
function CategoryShare({ forecast }: { forecast: StormCutoutForecast }) {
	const shares = (forecast.categoryShare ?? []).slice(0, 5)
	if (shares.length === 0) return null
	return (
		<div className="grid grid-flow-col auto-cols-fr gap-1 text-center pt-1">
			{shares.map(c => {
				const hex = c.category >= 0 ? stormCategoryHex(c.category, c.category === 0 ? 50 : 100) : "#94a3b8"
				return (
					<div key={c.t} className="rounded-lg border border-white/10 bg-white/[0.04] py-1">
						<div className="text-[10px] text-slate-400">{formatLocal(c.t)}</div>
						<div className="text-[11px] font-semibold" style={{ color: hex }}>{categoryLabel(c.category)}</div>
						<div className="text-[10px] text-slate-300">{Math.round(c.share * 100)}% of {c.members}</div>
					</div>
				)
			})}
		</div>
	)
}
